import type {
  ContentRecommendation,
  MasteryState,
  BehaviorPattern,
  ContextualFactor,
  AIError,
  ContentType,
} from '@coffeeplexity/shared';
import { logger } from '../utils/logger';
import type {
  IntelligentSearchResult,
  AdaptiveFilter,
  MasteryBasedFilter,
  DifficultyRange,
  SearchBehaviorPattern,
} from '@coffeeplexity/shared/types/intelligentSearch';

export interface AdaptiveFilteringConfig {
  masteryConfidenceThreshold: number;
  engagementWeights: EngagementWeights;
  contextualFactors: ContextualFactorConfig[];
  adaptationSensitivity: number;
  fallbackStrategy: 'rule_based' | 'curated_content' | 'popular_content';
}

export interface EngagementWeights {
  viewTime: number;
  completionRate: number;
  rating: number;
  shareCount: number;
  bookmarkCount: number;
  repeatViews: number;
}

export interface ContextualFactorConfig {
  type: string;
  weight: number;
  decayRate: number;
  maxAge: number; // in hours
}

export interface FilteredContent {
  content: ContentRecommendation[];
  appliedFilters: AppliedFilter[];
  confidenceScore: number;
  reasoning: string[];
  fallbackUsed: boolean;
}

export interface AppliedFilter {
  type: string;
  criteria: any;
  itemsFiltered: number;
  confidenceScore: number;
  reasoning: string;
}

export interface EngagementMetrics {
  contentId: string;
  viewCount: number;
  averageViewTime: number;
  completionRate: number;
  averageRating: number;
  shareCount: number;
  bookmarkCount: number;
  repeatViewRate: number;
  engagementScore: number;
  lastUpdated: string;
}

export interface ContentRankingFactors {
  masteryAlignment: number;
  engagementScore: number;
  contextualRelevance: number;
  difficultyFit: number;
  recencyBoost: number;
  socialProof: number;
  personalizedScore: number;
}

export interface ContextualSuggestion {
  id: string;
  contentId: string;
  type: 'follow_up' | 'prerequisite' | 'related' | 'next_step' | 'practice';
  title: string;
  description: string;
  confidence: number;
  reasoning: string;
  contextFactors: string[];
  estimatedRelevance: number;
}

/**
 * Adaptive Content Filtering Service
 * Implements mastery-based filtering, engagement ranking, and contextual suggestions
 */
export class AdaptiveContentFilteringService {
  private config: AdaptiveFilteringConfig;
  private engagementMetrics: Map<string, EngagementMetrics> = new Map();
  private filteringHistory: Map<string, AppliedFilter[]> = new Map();

  constructor(config: AdaptiveFilteringConfig) {
    this.config = config;
  }

  /**
   * Apply mastery-based content filtering with confidence thresholds
   */
  async applyMasteryBasedFiltering(
    content: ContentRecommendation[],
    masteryStates: MasteryState[],
    confidenceThreshold: number = this.config.masteryConfidenceThreshold
  ): Promise<FilteredContent> {
    const appliedFilters: AppliedFilter[] = [];
    const reasoning: string[] = [];
    let filteredContent = [...content];

    try {
      // Create mastery lookup for efficient access
      const masteryLookup = new Map(masteryStates.map(m => [m.skillId, m]));

      // Filter based on mastery alignment and confidence
      const masteryFiltered = filteredContent.filter(item => {
        const alignmentScores = item.skillAlignment.map(skill => {
          const mastery = masteryLookup.get(skill.skillId);
          if (!mastery) return 0.5; // Default neutral score

          // Check if mastery confidence meets threshold
          if (mastery.confidence < confidenceThreshold) {
            return 0.3; // Lower score for uncertain mastery
          }

          // Calculate alignment based on mastery level and content difficulty
          const masteryGap = Math.abs(
            mastery.masteryLevel - item.difficultyLevel / 10
          );
          const alignmentScore = Math.max(0, 1 - masteryGap);

          return alignmentScore * skill.alignmentScore;
        });

        const averageAlignment =
          alignmentScores.length > 0
            ? alignmentScores.reduce((sum, score) => sum + score, 0) /
              alignmentScores.length
            : 0.5;

        return averageAlignment >= 0.3; // Minimum alignment threshold
      });

      const filteredCount = filteredContent.length - masteryFiltered.length;
      if (filteredCount > 0) {
        appliedFilters.push({
          type: 'mastery_based',
          criteria: { confidenceThreshold, minAlignment: 0.3 },
          itemsFiltered: filteredCount,
          confidenceScore: 0.8,
          reasoning: `Filtered ${filteredCount} items with low mastery alignment or confidence`,
        });
        reasoning.push(
          `Applied mastery-based filtering with ${confidenceThreshold} confidence threshold`
        );
      }

      filteredContent = masteryFiltered;

      // Apply difficulty range filtering based on mastery
      const difficultyFiltered = this.applyDifficultyRangeFiltering(
        filteredContent,
        masteryStates,
        appliedFilters,
        reasoning
      );

      return {
        content: difficultyFiltered,
        appliedFilters,
        confidenceScore: this.calculateOverallConfidence(appliedFilters),
        reasoning,
        fallbackUsed: false,
      };
    } catch (error) {
      logger.error('Error in mastery-based filtering:', error);

      // Fallback to rule-based filtering
      return this.applyFallbackFiltering(content, 'mastery_filtering_error');
    }
  }

  /**
   * Apply difficulty range filtering based on mastery states
   */
  private applyDifficultyRangeFiltering(
    content: ContentRecommendation[],
    masteryStates: MasteryState[],
    appliedFilters: AppliedFilter[],
    reasoning: string[]
  ): ContentRecommendation[] {
    // Calculate optimal difficulty range based on mastery
    const averageMastery =
      masteryStates.length > 0
        ? masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
          masteryStates.length
        : 0.5;

    const difficultyRange: DifficultyRange = {
      min: Math.max(0, (averageMastery - 0.3) * 10),
      max: Math.min(10, (averageMastery + 0.4) * 10),
      optimal: averageMastery * 10,
      confidence: 0.8,
      reasoning: `Based on average mastery level of ${averageMastery.toFixed(2)}`,
    };

    const filtered = content.filter(item => {
      return (
        item.difficultyLevel >= difficultyRange.min &&
        item.difficultyLevel <= difficultyRange.max
      );
    });

    const filteredCount = content.length - filtered.length;
    if (filteredCount > 0) {
      appliedFilters.push({
        type: 'difficulty_range',
        criteria: difficultyRange,
        itemsFiltered: filteredCount,
        confidenceScore: difficultyRange.confidence,
        reasoning: `Filtered content outside difficulty range ${difficultyRange.min.toFixed(1)}-${difficultyRange.max.toFixed(1)}`,
      });
      reasoning.push(
        `Applied difficulty range filtering based on mastery levels`
      );
    }

    return filtered;
  }

  /**
   * Rank content based on engagement metrics and algorithms
   */
  async rankContentByEngagement(
    content: ContentRecommendation[],
    userId: string,
    behaviorPatterns: BehaviorPattern[] = []
  ): Promise<ContentRecommendation[]> {
    try {
      // Calculate engagement scores for each content item
      const rankedContent = content.map(item => {
        const engagementMetrics = this.engagementMetrics.get(item.contentId);
        const rankingFactors = this.calculateRankingFactors(
          item,
          engagementMetrics,
          behaviorPatterns
        );

        // Calculate weighted engagement score
        const engagementScore = this.calculateEngagementScore(rankingFactors);

        return {
          ...item,
          priority: engagementScore,
          metadata: {
            ...item.metadata,
            engagementScore,
            rankingFactors,
            rankedAt: new Date().toISOString(),
          },
        };
      });

      // Sort by engagement score (descending)
      return rankedContent.sort((a, b) => b.priority - a.priority);
    } catch (error) {
      logger.error('Error in engagement-based ranking:', error);

      // Fallback to original order with slight randomization
      return content.sort(() => Math.random() - 0.5);
    }
  }

  /**
   * Calculate ranking factors for content item
   */
  private calculateRankingFactors(
    content: ContentRecommendation,
    engagementMetrics?: EngagementMetrics,
    behaviorPatterns: BehaviorPattern[] = []
  ): ContentRankingFactors {
    // Mastery alignment score
    const masteryAlignment =
      content.skillAlignment.length > 0
        ? content.skillAlignment.reduce(
            (sum, skill) => sum + skill.alignmentScore,
            0
          ) / content.skillAlignment.length
        : 0.5;

    // Engagement score from metrics
    const engagementScore = engagementMetrics?.engagementScore || 0.5;

    // Contextual relevance based on behavior patterns
    const contextualRelevance = this.calculateContextualRelevance(
      content,
      behaviorPatterns
    );

    // Difficulty fit (how well content difficulty matches user level)
    const difficultyFit = this.calculateDifficultyFit(content);

    // Recency boost for newer content
    const recencyBoost = this.calculateRecencyBoost(content);

    // Social proof from ratings and popularity
    const socialProof = this.calculateSocialProof(content);

    // Personalized score from AI recommendation
    const personalizedScore = content.confidence || 0.5;

    return {
      masteryAlignment,
      engagementScore,
      contextualRelevance,
      difficultyFit,
      recencyBoost,
      socialProof,
      personalizedScore,
    };
  }

  /**
   * Calculate weighted engagement score
   */
  private calculateEngagementScore(factors: ContentRankingFactors): number {
    const weights = {
      masteryAlignment: 0.25,
      engagementScore: 0.2,
      contextualRelevance: 0.15,
      difficultyFit: 0.15,
      recencyBoost: 0.1,
      socialProof: 0.1,
      personalizedScore: 0.05,
    };

    return (
      factors.masteryAlignment * weights.masteryAlignment +
      factors.engagementScore * weights.engagementScore +
      factors.contextualRelevance * weights.contextualRelevance +
      factors.difficultyFit * weights.difficultyFit +
      factors.recencyBoost * weights.recencyBoost +
      factors.socialProof * weights.socialProof +
      factors.personalizedScore * weights.personalizedScore
    );
  }

  /**
   * Calculate contextual relevance based on behavior patterns
   */
  private calculateContextualRelevance(
    content: ContentRecommendation,
    behaviorPatterns: BehaviorPattern[]
  ): number {
    let relevanceScore = 0.5; // Base score

    // Check content type preferences
    const contentTypePatterns = behaviorPatterns.filter(
      p =>
        p.patternType === 'content_preference' &&
        p.pattern.contentType === content.contentType
    );

    if (contentTypePatterns.length > 0) {
      const avgPreference =
        contentTypePatterns.reduce((sum, p) => sum + p.confidence, 0) /
        contentTypePatterns.length;
      relevanceScore += avgPreference * 0.3;
    }

    // Check timing patterns
    const currentHour = new Date().getHours();
    const timingPatterns = behaviorPatterns.filter(
      p =>
        p.patternType === 'timing' &&
        Math.abs(p.pattern.preferredHour - currentHour) <= 2
    );

    if (timingPatterns.length > 0) {
      relevanceScore += 0.1;
    }

    return Math.max(0, Math.min(1, relevanceScore));
  }

  /**
   * Calculate difficulty fit score
   */
  private calculateDifficultyFit(content: ContentRecommendation): number {
    // This would typically use user's current skill level
    // For now, assume moderate difficulty preference
    const optimalDifficulty = 5; // 1-10 scale
    const difficultyGap = Math.abs(content.difficultyLevel - optimalDifficulty);
    return Math.max(0, 1 - difficultyGap / 5);
  }

  /**
   * Calculate recency boost for newer content
   */
  private calculateRecencyBoost(content: ContentRecommendation): number {
    const now = new Date();
    const contentDate = new Date(content.createdAt);
    const daysSinceCreation =
      (now.getTime() - contentDate.getTime()) / (1000 * 60 * 60 * 24);

    // Exponential decay with 14-day half-life
    return Math.exp(-daysSinceCreation / 14);
  }

  /**
   * Calculate social proof score
   */
  private calculateSocialProof(content: ContentRecommendation): number {
    const rating = content.metadata?.rating || 0;
    const reviewCount = content.metadata?.reviewCount || 0;
    const viewCount = content.metadata?.viewCount || 0;

    const ratingScore = rating / 5; // Normalize to 0-1
    const reviewScore = Math.min(1, reviewCount / 50); // Cap at 50 reviews
    const popularityScore = Math.min(1, viewCount / 1000); // Cap at 1000 views

    return ratingScore * 0.5 + reviewScore * 0.3 + popularityScore * 0.2;
  }

  /**
   * Generate contextual content suggestions based on current activity
   */
  async generateContextualSuggestions(
    currentContentId: string,
    userId: string,
    contextualFactors: ContextualFactor[],
    availableContent: ContentRecommendation[]
  ): Promise<ContextualSuggestion[]> {
    try {
      const suggestions: ContextualSuggestion[] = [];
      const currentContent = availableContent.find(
        c => c.contentId === currentContentId
      );

      if (!currentContent) {
        return suggestions;
      }

      // Generate follow-up suggestions
      const followUpSuggestions = this.generateFollowUpSuggestions(
        currentContent,
        availableContent,
        contextualFactors
      );
      suggestions.push(...followUpSuggestions);

      // Generate prerequisite suggestions
      const prerequisiteSuggestions = this.generatePrerequisiteSuggestions(
        currentContent,
        availableContent,
        contextualFactors
      );
      suggestions.push(...prerequisiteSuggestions);

      // Generate related content suggestions
      const relatedSuggestions = this.generateRelatedSuggestions(
        currentContent,
        availableContent,
        contextualFactors
      );
      suggestions.push(...relatedSuggestions);

      // Generate next step suggestions
      const nextStepSuggestions = this.generateNextStepSuggestions(
        currentContent,
        availableContent,
        contextualFactors
      );
      suggestions.push(...nextStepSuggestions);

      // Sort by confidence and relevance
      return suggestions
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 10); // Limit to top 10 suggestions
    } catch (error) {
      logger.error('Error generating contextual suggestions:', error);
      return [];
    }
  }

  /**
   * Generate follow-up content suggestions
   */
  private generateFollowUpSuggestions(
    currentContent: ContentRecommendation,
    availableContent: ContentRecommendation[],
    contextualFactors: ContextualFactor[]
  ): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];

    // Find content that builds on current content's skills
    const followUpContent = availableContent.filter(content => {
      if (content.id === currentContent.id) return false;

      // Check if content builds on current skills
      const skillOverlap = content.skillAlignment.some(skill =>
        currentContent.skillAlignment.some(
          currentSkill =>
            currentSkill.skillId === skill.skillId &&
            skill.alignmentScore > currentSkill.alignmentScore
        )
      );

      // Check if difficulty is slightly higher
      const difficultyProgression =
        content.difficultyLevel > currentContent.difficultyLevel &&
        content.difficultyLevel <= currentContent.difficultyLevel + 2;

      return skillOverlap && difficultyProgression;
    });

    followUpContent.slice(0, 3).forEach((content, index) => {
      suggestions.push({
        id: `followup_${content.id}_${index}`,
        contentId: content.contentId,
        type: 'follow_up',
        title: `Continue with: ${content.title}`,
        description: `Build on what you just learned with this next step`,
        confidence: 0.8 - index * 0.1,
        reasoning: `Builds on skills from current content with appropriate difficulty progression`,
        contextFactors: ['skill_progression', 'difficulty_match'],
        estimatedRelevance: 0.9 - index * 0.1,
      });
    });

    return suggestions;
  }

  /**
   * Generate prerequisite content suggestions
   */
  private generatePrerequisiteSuggestions(
    currentContent: ContentRecommendation,
    availableContent: ContentRecommendation[],
    contextualFactors: ContextualFactor[]
  ): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];

    // Find content that covers prerequisites for current content
    const prerequisiteContent = availableContent.filter(content => {
      if (content.id === currentContent.id) return false;

      // Check if content covers foundational skills
      const coversPrerequisites = content.skillAlignment.some(skill =>
        currentContent.skillAlignment.some(
          currentSkill =>
            currentSkill.skillId === skill.skillId &&
            skill.alignmentScore < currentSkill.alignmentScore
        )
      );

      // Check if difficulty is lower
      const isFoundational =
        content.difficultyLevel < currentContent.difficultyLevel;

      return coversPrerequisites && isFoundational;
    });

    prerequisiteContent.slice(0, 2).forEach((content, index) => {
      suggestions.push({
        id: `prerequisite_${content.id}_${index}`,
        contentId: content.contentId,
        type: 'prerequisite',
        title: `Foundation: ${content.title}`,
        description: `Master the basics before advancing further`,
        confidence: 0.7 - index * 0.1,
        reasoning: `Covers foundational skills needed for current content`,
        contextFactors: ['skill_foundation', 'difficulty_preparation'],
        estimatedRelevance: 0.8 - index * 0.1,
      });
    });

    return suggestions;
  }

  /**
   * Generate related content suggestions
   */
  private generateRelatedSuggestions(
    currentContent: ContentRecommendation,
    availableContent: ContentRecommendation[],
    contextualFactors: ContextualFactor[]
  ): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];

    // Find content with similar skills or topics
    const relatedContent = availableContent.filter(content => {
      if (content.id === currentContent.id) return false;

      // Check skill similarity
      const skillSimilarity = this.calculateSkillSimilarity(
        currentContent.skillAlignment,
        content.skillAlignment
      );

      // Check content type similarity
      const sameContentType =
        content.contentType === currentContent.contentType;

      // Check difficulty similarity
      const similarDifficulty =
        Math.abs(content.difficultyLevel - currentContent.difficultyLevel) <= 1;

      return skillSimilarity > 0.3 || (sameContentType && similarDifficulty);
    });

    relatedContent.slice(0, 3).forEach((content, index) => {
      suggestions.push({
        id: `related_${content.id}_${index}`,
        contentId: content.contentId,
        type: 'related',
        title: `Similar: ${content.title}`,
        description: `Explore related topics and techniques`,
        confidence: 0.6 - index * 0.1,
        reasoning: `Similar skills and difficulty level to current content`,
        contextFactors: ['skill_similarity', 'content_type_match'],
        estimatedRelevance: 0.7 - index * 0.1,
      });
    });

    return suggestions;
  }

  /**
   * Generate next step suggestions
   */
  private generateNextStepSuggestions(
    currentContent: ContentRecommendation,
    availableContent: ContentRecommendation[],
    contextualFactors: ContextualFactor[]
  ): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];

    // Find content that represents logical next steps
    const nextStepContent = availableContent.filter(content => {
      if (content.id === currentContent.id) return false;

      // Check if content type suggests progression (e.g., tutorial -> practice)
      const isProgressionType = this.isProgressionContentType(
        currentContent.contentType,
        content.contentType
      );

      // Check if skills are complementary
      const hasComplementarySkills = content.skillAlignment.some(
        skill =>
          !currentContent.skillAlignment.some(
            currentSkill => currentSkill.skillId === skill.skillId
          )
      );

      return isProgressionType || hasComplementarySkills;
    });

    nextStepContent.slice(0, 2).forEach((content, index) => {
      suggestions.push({
        id: `nextstep_${content.id}_${index}`,
        contentId: content.contentId,
        type: 'next_step',
        title: `Next: ${content.title}`,
        description: `Take the next step in your learning journey`,
        confidence: 0.7 - index * 0.1,
        reasoning: `Represents logical progression from current content`,
        contextFactors: ['learning_progression', 'skill_expansion'],
        estimatedRelevance: 0.8 - index * 0.1,
      });
    });

    return suggestions;
  }

  /**
   * Calculate skill similarity between two content items
   */
  private calculateSkillSimilarity(skills1: any[], skills2: any[]): number {
    if (skills1.length === 0 || skills2.length === 0) return 0;

    const skill1Ids = new Set(skills1.map(s => s.skillId));
    const skill2Ids = new Set(skills2.map(s => s.skillId));

    const intersection = new Set(
      [...skill1Ids].filter(id => skill2Ids.has(id))
    );
    const union = new Set([...skill1Ids, ...skill2Ids]);

    return intersection.size / union.size; // Jaccard similarity
  }

  /**
   * Check if content types represent a logical progression
   */
  private isProgressionContentType(
    currentType: ContentType,
    nextType: ContentType
  ): boolean {
    const progressionMap: Record<string, string[]> = {
      tutorial: ['practice', 'quiz', 'project'],
      video: ['practice', 'tutorial', 'quiz'],
      article: ['tutorial', 'practice', 'video'],
      practice: ['quiz', 'project', 'challenge'],
      quiz: ['practice', 'project', 'advanced_tutorial'],
    };

    return progressionMap[currentType]?.includes(nextType) || false;
  }

  /**
   * Update engagement metrics for content
   */
  updateEngagementMetrics(
    contentId: string,
    metrics: Partial<EngagementMetrics>
  ): void {
    const existing = this.engagementMetrics.get(contentId) || {
      contentId,
      viewCount: 0,
      averageViewTime: 0,
      completionRate: 0,
      averageRating: 0,
      shareCount: 0,
      bookmarkCount: 0,
      repeatViewRate: 0,
      engagementScore: 0,
      lastUpdated: new Date().toISOString(),
    };

    const updated = {
      ...existing,
      ...metrics,
      lastUpdated: new Date().toISOString(),
    };

    // Recalculate engagement score
    updated.engagementScore = this.calculateEngagementMetricScore(updated);

    this.engagementMetrics.set(contentId, updated);
  }

  /**
   * Calculate engagement score from metrics
   */
  private calculateEngagementMetricScore(metrics: EngagementMetrics): number {
    const weights = this.config.engagementWeights;

    const normalizedViewTime = Math.min(1, metrics.averageViewTime / 300); // 5 minutes max
    const normalizedRating = metrics.averageRating / 5;
    const normalizedShares = Math.min(1, metrics.shareCount / 10);
    const normalizedBookmarks = Math.min(1, metrics.bookmarkCount / 20);
    const normalizedRepeats = Math.min(1, metrics.repeatViewRate);

    return (
      normalizedViewTime * weights.viewTime +
      metrics.completionRate * weights.completionRate +
      normalizedRating * weights.rating +
      normalizedShares * weights.shareCount +
      normalizedBookmarks * weights.bookmarkCount +
      normalizedRepeats * weights.repeatViews
    );
  }

  /**
   * Apply fallback filtering when main filtering fails
   */
  private applyFallbackFiltering(
    content: ContentRecommendation[],
    errorReason: string
  ): FilteredContent {
    let fallbackContent: ContentRecommendation[];

    switch (this.config.fallbackStrategy) {
      case 'popular_content':
        fallbackContent = content
          .sort(
            (a, b) =>
              (b.metadata?.popularity || 0) - (a.metadata?.popularity || 0)
          )
          .slice(0, Math.min(20, content.length));
        break;

      case 'curated_content':
        fallbackContent = content
          .filter(c => c.metadata?.curated === true)
          .slice(0, Math.min(15, content.length));
        break;

      case 'rule_based':
      default:
        fallbackContent = content
          .sort((a, b) => b.confidence - a.confidence)
          .slice(0, Math.min(25, content.length));
        break;
    }

    return {
      content: fallbackContent,
      appliedFilters: [
        {
          type: 'fallback',
          criteria: {
            strategy: this.config.fallbackStrategy,
            reason: errorReason,
          },
          itemsFiltered: content.length - fallbackContent.length,
          confidenceScore: 0.5,
          reasoning: `Applied fallback filtering due to: ${errorReason}`,
        },
      ],
      confidenceScore: 0.5,
      reasoning: [
        `Fallback filtering applied: ${this.config.fallbackStrategy}`,
      ],
      fallbackUsed: true,
    };
  }

  /**
   * Calculate overall confidence score from applied filters
   */
  private calculateOverallConfidence(appliedFilters: AppliedFilter[]): number {
    if (appliedFilters.length === 0) return 1.0;

    const avgConfidence =
      appliedFilters.reduce((sum, filter) => sum + filter.confidenceScore, 0) /
      appliedFilters.length;

    return avgConfidence;
  }

  /**
   * Get filtering history for analytics
   */
  getFilteringHistory(userId: string): AppliedFilter[] {
    return this.filteringHistory.get(userId) || [];
  }

  /**
   * Clear filtering history
   */
  clearFilteringHistory(userId?: string): void {
    if (userId) {
      this.filteringHistory.delete(userId);
    } else {
      this.filteringHistory.clear();
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<AdaptiveFilteringConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

/**
 * Create default adaptive filtering service
 */
export function createAdaptiveFilteringService(): AdaptiveContentFilteringService {
  const defaultConfig: AdaptiveFilteringConfig = {
    masteryConfidenceThreshold: 0.7,
    engagementWeights: {
      viewTime: 0.25,
      completionRate: 0.3,
      rating: 0.2,
      shareCount: 0.1,
      bookmarkCount: 0.1,
      repeatViews: 0.05,
    },
    contextualFactors: [
      { type: 'time_of_day', weight: 0.1, decayRate: 0.1, maxAge: 24 },
      { type: 'session_length', weight: 0.15, decayRate: 0.05, maxAge: 48 },
      { type: 'recent_activity', weight: 0.2, decayRate: 0.2, maxAge: 72 },
    ],
    adaptationSensitivity: 0.8,
    fallbackStrategy: 'rule_based',
  };

  return new AdaptiveContentFilteringService(defaultConfig);
}

// Export singleton instance
export const adaptiveFilteringService = createAdaptiveFilteringService();
